import maplibregl from "maplibre-gl";
import {
  cardinalDirection,
  type WindLocation,
  weatherDescription,
} from "./windData";
import type { WindSpeedUnit } from "../ui/preferences";

export interface WindDisplaySample extends WindLocation {
  speedKnots: number;
  direction: number;
}

export interface WindDisplaySummary {
  modeLabel: string;
  speedKnots: number;
  direction: number;
  detail: string;
  providerLabel: string;
  providerUrl: string;
  speedUnit: WindSpeedUnit;
  weather?: {
    code: number;
    isDay: boolean;
    precipitationMm: number;
  };
}

function formatSpeed(speedKnots: number, unit: WindSpeedUnit): string {
  return unit === "ms"
    ? `${(speedKnots / 1.943_844_492_440_6).toFixed(1)} m/s`
    : `${speedKnots.toFixed(1)} kn`;
}

interface WindMarker {
  marker: maplibregl.Marker;
  arrow: HTMLElement;
  direction: number;
}

class WindSummaryControl implements maplibregl.IControl {
  readonly element = document.createElement("section");

  onAdd(): HTMLElement {
    this.element.className = "maplibregl-ctrl wind-summary";
    this.element.hidden = true;
    this.element.setAttribute("aria-live", "polite");
    this.element.setAttribute("aria-label", "Wind");
    return this.element;
  }

  onRemove(): void {
    this.element.remove();
  }
}

/** DOM markers keep the wind arrows crisp without adding another sprite asset. */
export class WindOverlay {
  private readonly control = new WindSummaryControl();
  private readonly markers: WindMarker[] = [];
  private summaryExpanded = false;

  private readonly orientMarkers = (): void => {
    const bearing = this.map.getBearing();
    for (const marker of this.markers) {
      marker.arrow.style.transform = `rotate(${marker.direction - bearing}deg)`;
    }
  };

  constructor(private readonly map: maplibregl.Map) {
    this.map.addControl(this.control, "top-right");
    this.map.on("rotate", this.orientMarkers);
  }

  showLoading(modeLabel: string): void {
    this.clearMarkers();
    this.summaryExpanded = false;
    this.control.element.hidden = false;
    delete this.control.element.dataset.expanded;
    this.control.element.dataset.state = "loading";
    const mode = document.createElement("strong");
    mode.textContent = modeLabel;
    const status = document.createElement("span");
    status.className = "wind-summary-status";
    status.textContent = "Loading wind…";
    this.control.element.replaceChildren(mode, status);
  }

  showError(modeLabel: string, message: string): void {
    this.clearMarkers();
    this.summaryExpanded = false;
    this.control.element.hidden = false;
    delete this.control.element.dataset.expanded;
    this.control.element.dataset.state = "error";
    const mode = document.createElement("strong");
    mode.textContent = modeLabel;
    const status = document.createElement("span");
    status.className = "wind-summary-status";
    status.textContent = message;
    this.control.element.replaceChildren(mode, status);
  }

  showData(samples: WindDisplaySample[], summary: WindDisplaySummary): void {
    this.setMarkers(samples, summary.speedUnit);
    this.control.element.hidden = false;
    this.control.element.dataset.state = "ready";

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "wind-summary-toggle";
    const symbol = document.createElement("span");
    symbol.className = "wind-summary-symbol";
    symbol.setAttribute("aria-hidden", "true");
    symbol.textContent = "↓";
    symbol.style.transform = `rotate(${summary.direction}deg)`;
    const compactSpeed = document.createElement("span");
    compactSpeed.className = "wind-summary-compact-speed";
    compactSpeed.textContent = formatSpeed(summary.speedKnots, summary.speedUnit);
    toggle.append(symbol, compactSpeed);

    const expanded = document.createElement("div");
    expanded.className = "wind-summary-expanded";
    const mode = document.createElement("strong");
    mode.className = "wind-summary-mode";
    mode.textContent = summary.modeLabel;
    const speed = document.createElement("span");
    speed.className = "wind-summary-speed";
    speed.textContent = formatSpeed(summary.speedKnots, summary.speedUnit);
    const direction = document.createElement("span");
    direction.className = "wind-summary-detail";
    direction.textContent = cardinalDirection(summary.direction);
    if (summary.weather) {
      const weather = weatherDescription(
        summary.weather.code,
        summary.weather.isDay,
        summary.weather.precipitationMm,
      );
      const icon = document.createElement("span");
      icon.className = "wind-summary-weather";
      icon.textContent = weather.icon;
      icon.title = weather.label;
      icon.setAttribute("aria-label", weather.label);
      direction.append(" · ", icon, ` ${weather.label}`);
    }
    const detail = document.createElement("span");
    detail.className = "wind-summary-meta";
    detail.textContent = summary.detail;
    const attribution = document.createElement("a");
    attribution.className = "wind-summary-attribution";
    attribution.href = summary.providerUrl;
    attribution.target = "_blank";
    attribution.rel = "noreferrer";
    attribution.textContent = summary.providerLabel;
    expanded.append(mode, speed, direction, detail, attribution);
    toggle.addEventListener("click", () => {
      this.summaryExpanded = !this.summaryExpanded;
      this.updateSummaryExpansion(summary);
    });
    this.control.element.replaceChildren(toggle, expanded);
    this.updateSummaryExpansion(summary);
  }

  clear(): void {
    this.clearMarkers();
    this.summaryExpanded = false;
    this.control.element.hidden = true;
    this.control.element.replaceChildren();
    delete this.control.element.dataset.state;
  }

  destroy(): void {
    this.clearMarkers();
    this.map.off("rotate", this.orientMarkers);
    if (this.map.hasControl(this.control)) this.map.removeControl(this.control);
  }

  private updateSummaryExpansion(summary: WindDisplaySummary): void {
    const expanded = this.control.element.querySelector<HTMLElement>(".wind-summary-expanded");
    const toggle = this.control.element.querySelector<HTMLButtonElement>(".wind-summary-toggle");
    if (!expanded || !toggle) return;
    expanded.hidden = !this.summaryExpanded;
    this.control.element.dataset.expanded = String(this.summaryExpanded);
    toggle.setAttribute("aria-expanded", String(this.summaryExpanded));
    const action = this.summaryExpanded ? "Hide wind details" : "Show wind details";
    toggle.setAttribute(
      "aria-label",
      `${formatSpeed(summary.speedKnots, summary.speedUnit)}, ${cardinalDirection(summary.direction)} wind. ${action}`,
    );
    toggle.title = action;
  }

  private setMarkers(samples: WindDisplaySample[], unit: WindSpeedUnit): void {
    this.clearMarkers();
    for (const sample of samples) {
      const element = document.createElement("div");
      element.className = "wind-map-marker";
      element.dataset.speedKnots = sample.speedKnots.toFixed(1);
      element.dataset.direction = sample.direction.toFixed(0);
      element.setAttribute("role", "img");
      element.setAttribute(
        "aria-label",
        `${cardinalDirection(sample.direction)} wind, ${formatSpeed(sample.speedKnots, unit)}`,
      );
      const arrow = document.createElement("span");
      arrow.className = "wind-map-arrow";
      const shaft = document.createElement("i");
      shaft.className = "wind-map-arrow-shaft";
      const head = document.createElement("i");
      head.className = "wind-map-arrow-head";
      arrow.append(shaft, head);
      element.append(arrow);
      const marker = new maplibregl.Marker({
        element,
        anchor: "center",
        pitchAlignment: "viewport",
        rotationAlignment: "viewport",
      })
        .setLngLat([sample.longitude, sample.latitude])
        .addTo(this.map);
      this.markers.push({ marker, arrow, direction: sample.direction });
    }
    this.orientMarkers();
  }

  private clearMarkers(): void {
    for (const { marker } of this.markers) marker.remove();
    this.markers.length = 0;
  }
}

/** Fifteen screen-distributed points, including the exact display centre. */
export function windGridLocations(map: maplibregl.Map): WindLocation[] {
  const container = map.getContainer();
  const portrait = container.clientHeight > container.clientWidth * 1.2;
  const columns = portrait ? 3 : 5;
  const rows = portrait ? 5 : 3;
  const points: WindLocation[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const x = container.clientWidth * (0.15 + (0.7 * column) / (columns - 1));
      const y = container.clientHeight * (0.15 + (0.7 * row) / (rows - 1));
      const location = map.unproject([x, y]);
      points.push({ longitude: location.lng, latitude: location.lat });
    }
  }
  return points;
}
